import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { API } from "@/assets";
import { useContent } from "@/components/useContent";
import { CalendarClock, Loader2, User, Mic2, Building2 } from "lucide-react";

const SESSIONS = [
  { id: "consult_heather", label: "Consultation — Heather Da Boa", sub: "Production & sound strategy · 45 min", price: 75, Icon: Mic2 },
  { id: "consult_hotrod", label: "Consultation — Sherrod 'HotRod' Whiteside", sub: "Business & artist development · 45 min", price: 75, Icon: User },
  { id: "studio_time", label: "Studio Time Block", sub: "In the DGE lab · 2 hours", price: 150, Icon: Building2 },
];

const TIME_SLOTS = ["10:00 AM", "12:00 PM", "2:00 PM", "4:00 PM", "6:00 PM", "8:00 PM"];

const inputCls =
  "w-full bg-obsidian/70 border border-white/12 rounded-xl px-4 py-3.5 text-sm text-ice placeholder:text-ice/35 focus:outline-none focus:border-venom/70 focus:ring-2 focus:ring-venom/20 transition-all duration-300";

export const BookingSection = () => {
  const content = useContent();
  const intro = content.booking_intro || "Paid consultations and studio time with the people who run the label. Pick your session, lock your slot, pay securely — we confirm within 24–48 hours.";
  const [session, setSession] = useState("consult_heather");
  const [form, setForm] = useState({ name: "", email: "", date: "", time_slot: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const selected = SESSIONS.find((s) => s.id === session);
  const today = new Date().toISOString().split("T")[0];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.date || !form.time_slot) {
      setError("Please pick a date and a time slot.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, booking_type: session }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(typeof d.detail === "string" ? d.detail : "Booking failed. Please try again.");
      }
      const booking = await res.json();
      const ck = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lookup_key: booking.lookup_key,
          booking_id: booking.booking_id,
          origin_url: window.location.origin,
        }),
      });
      if (!ck.ok) throw new Error("Could not start checkout. Please try again.");
      const { checkout_url } = await ck.json();
      window.location.href = checkout_url;
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" className="relative py-20 lg:py-28 bg-charcoal/40" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20">
        <Reveal className="min-w-0">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium mb-4">
            Book a Session
          </p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight">
            Sit with the
            <br />
            <span className="text-stroke-crystal">architects.</span>
          </h2>
          <p className="mt-6 text-sm sm:text-base text-ice/70 leading-relaxed max-w-md">
            {intro}
          </p>
          <div className="mt-8 space-y-3">
            {SESSIONS.map((s) => {
              const Icon = s.Icon;
              const active = session === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSession(s.id)}
                  className={`w-full flex items-center gap-4 rounded-xl border p-4 text-left transition-all duration-300 ${
                    active
                      ? "border-venom/60 bg-venom/8 glow-venom"
                      : "border-crystal/25 bg-charcoal hover:border-crystal/60"
                  }`}
                  data-testid={`booking-session-${s.id}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border ${active ? "border-venom/50 bg-venom/15" : "border-crystal/40 bg-crystal/10"}`}>
                    <Icon size={18} className="text-venom" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-white text-xs sm:text-sm font-bold truncate">{s.label}</p>
                    <p className="text-[11px] text-ice/50 mt-0.5">{s.sub}</p>
                  </div>
                  <span className="font-display text-venom font-bold text-sm shrink-0">${s.price}</span>
                </button>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="min-w-0">
          <form
            onSubmit={submit}
            className="bg-charcoal border border-crystal/25 rounded-2xl p-6 sm:p-9 shadow-[0_20px_80px_rgba(0,0,0,0.5)]"
            data-testid="booking-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="booking-name" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-ice/60 mb-2">Name *</label>
                <input id="booking-name" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="booking-input-name" />
              </div>
              <div>
                <label htmlFor="booking-email" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-ice/60 mb-2">Email *</label>
                <input id="booking-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} data-testid="booking-input-email" />
              </div>
              <div>
                <label htmlFor="booking-date" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-ice/60 mb-2">Date *</label>
                <input id="booking-date" type="date" required min={today} value={form.date} onChange={set("date")} className={`${inputCls} [color-scheme:dark]`} data-testid="booking-input-date" />
              </div>
              <div>
                <label htmlFor="booking-slot" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-ice/60 mb-2">Time Slot *</label>
                <select id="booking-slot" value={form.time_slot} onChange={set("time_slot")} className={`${inputCls} dge-select pr-10`} data-testid="booking-select-slot">
                  <option value="" disabled>Pick a slot</option>
                  {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="booking-notes" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-ice/60 mb-2">Notes (optional)</label>
                <textarea id="booking-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder="What do you want to get out of this session?" className={`${inputCls} resize-none`} data-testid="booking-input-notes" />
              </div>
            </div>

            {error && (
              <p className="mt-4 text-sm text-red-400" role="alert" data-testid="booking-form-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-7 w-full inline-flex items-center justify-center gap-2.5 bg-venom text-obsidian font-bold text-sm sm:text-base px-8 py-4 rounded-full hover:glow-venom hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
              data-testid="booking-submit-button"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <CalendarClock size={17} />}
              {submitting ? "Opening secure checkout…" : `Pay & Book — $${selected.price}`}
            </button>
            <p className="mt-3 text-center text-[11px] text-ice/40">
              Secure payment via Stripe · Confirmation email within minutes
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
